<script setup lang="ts">
import {computed} from "vue";
import {CircleCheck, Eye} from "@lucide/vue";
import MedicationCheckupPosologyGlance from "@/medication-checkup/components/MedicationCheckupPosologyGlance.vue";
import MedicationCheckupPosologyLine from "@/medication-checkup/components/MedicationCheckupPosologyLine.vue";
import {TreatmentLine} from "@/medication-checkup/interfaces/treatment-selections";
import {LinePosology} from "@/medication-checkup/services/LinePosologies";
import TreatmentSheets, {TreatmentSheet} from "@/medication-checkup/services/TreatmentSheets";

/**
 * The treatments retained for the medication review, one row each with its
 * posology in words and drawn — what the collection hands on to the
 * analysis. Before validation a row still asking for its posology says so
 * and opens the dialog; once validated the record reads only, and each
 * scanned prescription can be opened again.
 */

const props = defineProps<{
    entries: {line: TreatmentLine; posology: LinePosology}[];
    editable?: boolean;
}>();

const emit = defineEmits<{
    edit: [line: TreatmentLine];
    view: [sheets: TreatmentSheet[]];
}>();

/**
 * A hand-added line wears the pharmacist's tone, a collected one the tone of
 * what the collection found.
 */
const tone = (line: TreatmentLine): string => line.collected ? 'text-secondary-darkest' : 'text-primary';

/**
 * The scanned prescriptions of each line, by id — what its eye opens.
 */
const sheets = computed(() => new Map<number, TreatmentSheet[]>(
    props.entries.map(({line}) => [line.id, TreatmentSheets.sheetsOf(line)])
));
</script>

<template>
  <div class="flex flex-col">
    <div
      v-for="{line, posology} in entries"
      :key="line.id"
      class="flex items-center gap-2.5 border-b border-solid border-gray-light px-3 py-2 last:border-b-0"
      :class="{'bg-red-light/5': editable && !posology.draft}"
    >
      <CircleCheck class="size-[17px] shrink-0" :class="tone(line)" />

      <span class="flex min-w-0 flex-1 flex-col gap-0.5">
        <span class="text-[12.5px] font-semibold text-gray-dark">{{ line.label }}</span>
        <MedicationCheckupPosologyLine
          :posology="posology"
          :editable="editable"
          asked
          @edit="emit('edit', line)"
        />
      </span>

      <MedicationCheckupPosologyGlance v-if="posology.draft" :data="posology.draft.data" />

      <button
        v-if="!editable && sheets.get(line.id)?.length"
        type="button"
        class="shrink-0 text-primary hover:opacity-70"
        :title="(sheets.get(line.id)?.length ?? 0) > 1 ? 'Voir les ordonnances' : 'Voir l\'ordonnance'"
        @click="emit('view', sheets.get(line.id) ?? [])"
      >
        <Eye class="size-4" />
      </button>
    </div>
  </div>
</template>
